import { NavLink } from "react-router-dom";
import type { CompanyNavItem } from "../../types/company";
import { cn } from "../../lib/format";
import { useT } from "../../i18n";
import SideNav from "../ui/SideNav";

interface CompanySidebarProps {
  items: CompanyNavItem[];
  /** e.g. "5 active listings · Free launch access" */
  usage: string;
  activeListings: number;
  /** Max active listings; null = unlimited (full bar), 0 = no plan. */
  limit: number | null;
  /** Phones: the slide-in panel is open */
  open: boolean;
  onClose: () => void;
  companyName: string;
}

/** Sidebar on desktop, slide-in panel (header menu button) on phones and tablets. */
export default function CompanySidebar({
  items,
  usage,
  activeListings,
  limit,
  open,
  onClose,
  companyName,
}: CompanySidebarProps) {
  const t = useT();
  const atLimit = !!limit && activeListings >= limit;
  const usagePct = limit === null ? 100 : limit ? Math.min(100, (activeListings / limit) * 100) : 0;

  return (
    <SideNav open={open} onClose={onClose} label={t.company.nav.aria} title={companyName || t.company.nav.aria}>
      {items.map((item) => (
        <NavLink
          key={item.href}
          to={item.href}
          end={item.href === "/company"}
          className={({ isActive }) =>
            cn(
              "flex items-center justify-between gap-2.5 whitespace-nowrap rounded px-3 py-2.5 text-sm font-medium text-ink no-underline transition-colors hover:bg-[#EDEDED]",
              // Current page: white with a brand edge, so it stands out from hover
                  isActive && "bg-white text-brand-link shadow-[inset_3px_0_0_var(--color-brand),0_1px_2px_rgba(0,0,0,0.06)] hover:bg-white",
            )
          }
        >
          {t.company.nav[item.key]}
          {!!item.count && (
            <span className="inline-flex h-4.5 min-w-4.5 items-center justify-center rounded-full bg-brand px-1.25 text-[11px] text-white">
              {item.count}
            </span>
          )}
        </NavLink>
      ))}


      <div className="mt-5 border-t border-line-strong px-3 pt-3.5 text-xs leading-relaxed text-muted">
        {usage}
        <div className="mt-1.5 h-1 overflow-hidden rounded-sm bg-line-strong">
          <div
            className={cn("h-full", atLimit ? "bg-tone-red-fg" : "bg-brand")}
            style={{ width: `${usagePct}%` }}
          />
        </div>
      </div>
    </SideNav>
  );
}
